// Rutas con puntos para nombrar una clave del guion.
//
// ESPEJO de omnibot-platform/tenant/ruta.go. Las dos tienen que escapar igual:
// el servidor guarda con esas claves y el navegador las construye para pedir un
// cambio, así que una discrepancia manda el texto a otro sitio del guion — sin
// error, con el guion válido, y el cliente siguiendo con el mensaje viejo.
//
// Por qué hace falta escapar: un punto separa niveles, pero los nombres de
// mensaje pueden llevar punto — la consola bautiza los que traen las
// capacidades como `<capacidad>.<rol>`. Sin distinguirlos,
// "plantillas.consultar_cartera.ok" significa dos cosas a la vez.
//
//	plantillas.menu_principal          plantillas > menu_principal
//	plantillas.consultar_cartera\.ok   plantillas > consultar_cartera.ok

function escapar(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/\./g, '\\.')
}

/** Une segmentos en una ruta, escapando lo que haga falta. */
export function ruta(...segmentos: string[]): string {
  return segmentos.map(escapar).join('.')
}

/** Deshace `ruta`: devuelve los nombres tal como son, sin escapes. */
export function segmentos(r: string): string[] {
  if (!r) return []
  const out: string[] = []
  let actual = ''
  let escapando = false
  for (const c of r) {
    if (escapando) { actual += c; escapando = false; continue }
    if (c === '\\') { escapando = true; continue }
    if (c === '.') { out.push(actual); actual = ''; continue }
    actual += c
  }
  // Una barra suelta al final se toma como literal en vez de perderse: perder
  // un carácter en silencio es peor que un nombre raro.
  if (escapando) actual += '\\'
  out.push(actual)
  return out
}

/** El último segmento — el nombre a secas. Para "plantillas.saludo", "saludo". */
export function nombreDe(r: string): string {
  const s = segmentos(r)
  return s[s.length - 1] ?? ''
}

/** Espejo de `EsAncestro` en omnibot-platform/tenant/ruta.go: ¿`arriba` nombra
 *  una rama por encima de `abajo`? Dos claves iguales no lo son. */
function esAncestro(arriba: string, abajo: string): boolean {
  if (!arriba || arriba === abajo) return false
  const a = segmentos(arriba)
  const b = segmentos(abajo)
  if (a.length >= b.length) return false
  return a.every((s, i) => s === b[i])
}

/** Con qué choca `clave` dentro de `existentes`, o `null` si está libre.
 *
 *  `misma` distingue los dos choques, porque piden cosas opuestas en pantalla:
 *  la clave que YA ESTÁ se edita donde está —tiene su valor y su procedencia a
 *  la vista—, mientras que la que se PISA con otra no se puede guardar de
 *  ninguna forma hasta quitar una de las dos. El servidor rechaza la segunda;
 *  la primera la aceptaría sobrescribiendo en silencio, que es justo lo que
 *  «Añadir clave» no debe hacer. */
export function choqueDeClave(
  clave: string,
  existentes: string[],
): { con: string; misma: boolean } | null {
  // La coincidencia exacta se busca primero y en su propia pasada: una clave
  // puede ser a la vez idéntica a una y rama de otra, y contarla como choque de
  // ramas mandaría a quitar una fila cuando lo que hace falta es editarla.
  if (existentes.includes(clave)) return { con: clave, misma: true }
  for (const e of existentes) {
    if (esAncestro(e, clave) || esAncestro(clave, e)) return { con: e, misma: false }
  }
  return null
}
